import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { TocEntry } from './LegalLayout';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { company, legalPaths } from '../data/company';

const TOC: TocEntry[] = [
  { id: 'acknowledgment', label: 'Acknowledgment' },
  { id: 'definitions', label: 'Definitions' },
  { id: 'use-of-site', label: 'Permitted use' },
  { id: 'no-advice', label: 'No professional advice' },
  { id: 'intellectual-property', label: 'Intellectual property' },
  { id: 'client-work', label: 'Client engagements' },
  { id: 'links', label: 'Links to other websites' },
  { id: 'availability', label: 'Availability and changes' },
  { id: 'disclaimer', label: '"As is" disclaimer' },
  { id: 'liability', label: 'Limitation of liability' },
  { id: 'privacy', label: 'Privacy and cookies' },
  { id: 'governing-law', label: 'Governing law' },
  { id: 'consumers', label: 'EU consumer rights' },
  { id: 'disputes', label: 'Disputes' },
  { id: 'severability', label: 'Severability and waiver' },
  { id: 'translation', label: 'Translation' },
  { id: 'changes', label: 'Changes to these terms' },
  { id: 'contact', label: 'Contact us' },
];

const TermsOfService: React.FC = () => {
  useDocumentTitle(
    'Terms of Use — Parnasa Apps',
    'The terms on which you may use the Parnasa Apps website.'
  );

  return (
    <LegalLayout
      kicker="Legal · Terms"
      title="Terms of Use"
      intro="The terms on which you may use this website. Please read them before continuing — using the site means you accept them."
      toc={TOC}
    >
      <h2 id="acknowledgment">Acknowledgment</h2>
      <p>
        These Terms of Use (&ldquo;Terms&rdquo;) govern your use of {company.url} (the
        &ldquo;Website&rdquo;), operated by {company.legalName} of {company.addressOneLine}. They form
        the agreement between you and us regarding the Website, and they apply to every visitor.
      </p>
      <p>
        By accessing or using the Website you agree to be bound by these Terms. If you do not agree with
        any part of them, please do not use the Website.
      </p>
      <p>
        Your use of the Website is also subject to our{' '}
        <Link to={legalPaths.privacy}>Privacy Policy</Link> and{' '}
        <Link to={legalPaths.cookies}>Cookie Policy</Link>, which explain how we handle personal data
        and what is stored on your device.
      </p>

      <h2 id="definitions">Definitions</h2>
      <ul>
        <li>
          <strong>Company</strong> (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) means{' '}
          {company.legalName}, {company.addressOneLine}.
        </li>
        <li>
          <strong>Website</strong> or <strong>Service</strong> means {company.url} and its content.
        </li>
        <li>
          <strong>You</strong> means the individual using the Website, or the organisation on whose
          behalf they act.
        </li>
        <li>
          <strong>Content</strong> means the text, design, images, code, marks and other material made
          available on the Website.
        </li>
        <li>
          <strong>Country</strong> means Italy.
        </li>
      </ul>

      <h2 id="use-of-site">Permitted use</h2>
      <p>
        The Website is provided for information about our services. You may browse it, and copy or print
        reasonable extracts for your own internal, non-commercial reference.
      </p>
      <p>You agree not to:</p>
      <ul>
        <li>use the Website in breach of any applicable law or regulation;</li>
        <li>
          attempt to gain unauthorised access to the Website, its servers, or any system or network
          connected to it;
        </li>
        <li>
          interfere with the Website&rsquo;s operation, including by introducing malware or by
          overloading it with automated requests;
        </li>
        <li>
          scrape, harvest or systematically extract Content for republication or to train a commercial
          model, without our prior written permission;
        </li>
        <li>
          remove or obscure any copyright, trade mark or other proprietary notice, or present our
          Content as your own;
        </li>
        <li>
          use our name, logo or marks in a way that suggests a partnership, endorsement or affiliation
          that does not exist.
        </li>
      </ul>

      <h2 id="no-advice">No professional advice</h2>
      <p>
        The Content is general information about our work. It is not technical, legal, financial or
        other professional advice, and it should not be relied on for a specific decision. Descriptions
        of services, technologies and methods are illustrative and may change. For advice on your own
        situation, engage us — or another qualified adviser — under a contract.
      </p>

      <h2 id="intellectual-property">Intellectual property</h2>
      <p>
        The Website and its Content are owned by us or our licensors and are protected by copyright,
        trade mark and other intellectual property laws. The name &ldquo;{company.name}&rdquo;, the
        Parnasa logo and our visual identity are our marks and may not be used without our written
        permission.
      </p>
      <p>
        Except for the limited permission in <a href="#use-of-site">Permitted use</a>, nothing on the
        Website grants you a licence to reproduce, distribute, modify or create derivative works from
        the Content. Third-party names and marks mentioned on the Website — platforms, frameworks and
        tools — remain the property of their respective owners and are referred to descriptively only.
      </p>

      <h2 id="client-work">Client engagements</h2>
      <p>
        These Terms cover the Website only. Any work we carry out for you — design, engineering,
        consultancy or support — is governed by a separate written agreement covering scope, fees,
        timelines, acceptance, intellectual property, confidentiality and, where personal data is
        involved, a data processing agreement under Article 28 GDPR.
      </p>
      <p>
        Nothing on the Website is an offer capable of acceptance, and no quotation, estimate or
        indicative timeline given here or by email is binding until set out in a signed agreement.
      </p>

      <h2 id="links">Links to other websites</h2>
      <p>
        The Website may link to third-party sites or resources we neither own nor control. We do not
        endorse them and are not responsible for their content, availability, terms or privacy
        practices. Any dealings with a third party are between you and that party. We recommend reading
        the terms and privacy policy of any site you visit.
      </p>

      <h2 id="availability">Availability and changes</h2>
      <p>
        We aim to keep the Website available, but we do not guarantee uninterrupted access. We may
        change, suspend, restrict or withdraw all or part of the Website — including any Content — at
        any time, and we may do so without notice where necessary for maintenance or security. We may
        also terminate or restrict your access if you breach these Terms.
      </p>

      <h2 id="disclaimer">&ldquo;As is&rdquo; disclaimer</h2>
      <p>
        To the fullest extent permitted by applicable law, the Website is provided &ldquo;as is&rdquo;
        and &ldquo;as available&rdquo;, without warranties of any kind, whether express, implied or
        statutory, including implied warranties of merchantability, fitness for a particular purpose,
        title and non-infringement.
      </p>
      <p>
        We do not warrant that the Website will be uninterrupted, timely, error-free or free of harmful
        components, nor that the Content is complete, current or accurate at every moment. Some
        jurisdictions do not allow the exclusion of certain warranties or of the statutory rights of a
        consumer; in that case the exclusions above apply only to the extent permitted, and your
        statutory rights are unaffected.
      </p>

      <h2 id="liability">Limitation of liability</h2>
      <p>
        To the fullest extent permitted by applicable law, we shall not be liable for any indirect,
        incidental, special or consequential loss, nor for loss of profit, revenue, data, business or
        goodwill, arising out of or in connection with your use of, or inability to use, the Website —
        even if we have been advised of the possibility of such loss.
      </p>
      <p>
        Where liability cannot lawfully be excluded, our total aggregate liability arising out of or in
        connection with the Website is limited to EUR 100.
      </p>
      <p>
        Nothing in these Terms excludes or limits our liability for death or personal injury caused by
        negligence, for fraud or fraudulent misrepresentation, for gross negligence or wilful
        misconduct, or for any other liability that cannot be excluded or limited under Italian or EU
        law. This section does not affect the terms of any separate client agreement, which governs
        liability for the work performed under it.
      </p>

      <h2 id="privacy">Privacy and cookies</h2>
      <p>
        We process personal data as described in our{' '}
        <Link to={legalPaths.privacy}>Privacy Policy</Link>. Cookies and similar technologies are
        described in our <Link to={legalPaths.cookies}>Cookie Policy</Link>, and non-essential storage
        is used only with your consent, which you can withdraw at any time via &ldquo;Cookie
        settings&rdquo; in the footer.
      </p>

      <h2 id="governing-law">Governing law</h2>
      <p>
        These Terms and your use of the Website are governed by the laws of Italy, excluding its
        conflict-of-law rules. If you are a consumer resident in the European Union, this choice does
        not deprive you of the protection of the mandatory consumer law of your country of residence.
      </p>

      <h2 id="consumers">EU consumer rights</h2>
      <p>
        If you are a consumer in the European Union, you benefit from any mandatory provisions of the law
        of your country of residence, and nothing in these Terms limits those rights. In particular, the
        limitations in these Terms apply only to the extent permitted by applicable consumer protection
        law. The Website is informational and offers nothing for sale, so no right of withdrawal under
        the Consumer Rights Directive arises from browsing it.
      </p>

      <h2 id="disputes">Disputes</h2>
      <p>
        If you have a concern or a dispute about the Website, please contact us first at{' '}
        <a href={`mailto:${company.email}`}>{company.email}</a> — most matters are resolved quickly and
        informally. Where a dispute cannot be resolved that way, the courts of Rome, Italy shall have
        jurisdiction, save that a consumer may also bring proceedings in the courts of their place of
        residence where applicable law so provides.
      </p>

      <h2 id="severability">Severability and waiver</h2>
      <p>
        If any provision of these Terms is held invalid or unenforceable, it shall be interpreted to
        achieve its objective as far as applicable law permits, and the remaining provisions continue in
        full force.
      </p>
      <p>
        A failure to exercise a right, or to require performance of an obligation, does not waive that
        right or obligation for the future, and a waiver of one breach does not waive any subsequent
        breach.
      </p>

      <h2 id="translation">Translation</h2>
      <p>
        These Terms may be made available in translation. Where there is any conflict between versions,
        the original English text prevails, except where mandatory local law provides otherwise for
        consumers.
      </p>

      <h2 id="changes">Changes to these terms</h2>
      <p>
        We may modify these Terms at any time. The current version is always published on this page with
        a &ldquo;Last updated&rdquo; date. Where a revision is material, we will make reasonable efforts
        to signal it on the Website before it takes effect. By continuing to use the Website afterwards,
        you accept the revised Terms; if you do not accept them, please stop using the Website.
      </p>

      <h2 id="contact">Contact us</h2>
      <p>For any question about these Terms:</p>
      <ul>
        <li>
          <strong>Email:</strong> <a href={`mailto:${company.email}`}>{company.email}</a>
        </li>
        <li>
          <strong>Post:</strong> {company.legalName}, {company.addressOneLine}
        </li>
      </ul>
    </LegalLayout>
  );
};

export default TermsOfService;
